import { hu } from '~/locales/hu'
import { en } from '~/locales/en'
import { de } from '~/locales/de'
import type { Messages } from '~/locales/hu'

export type Locale = 'hu' | 'en' | 'de'

export const SITE_ORIGIN = 'https://archestor.com'
export const CONTACT_EMAIL = 'office@archestor.com'
export const CONTACT_API_URL = `${SITE_ORIGIN}/api/contact`

export const PRIMARY_LOCALE: Locale = 'hu'
export const FALLBACK_LOCALE: Locale = 'en'

export const LOCALES: { code: Locale; label: string }[] = [
  { code: 'hu', label: 'HU' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' }
]

export const LOCALIZED_SEGMENTS = ['en', 'de'] as const
export type LocalizedSegment = (typeof LOCALIZED_SEGMENTS)[number]

export function isLocalizedSegment(val: string | null | undefined): val is LocalizedSegment {
  return LOCALIZED_SEGMENTS.includes(val as LocalizedSegment)
}

const STORAGE_KEY = 'archestor-locale'

const localeMessages: Record<Locale, Messages> = { hu, en, de }

const cookieOptions = {
  maxAge: 60 * 60 * 24 * 365,
  path: '/',
  sameSite: 'lax' as const
}

export function localePath(locale: Locale): string {
  return locale === PRIMARY_LOCALE ? '/' : `/${locale}`
}

const HREFLANG_CODES: Record<Locale, string> = {
  hu: 'hu-HU',
  en: 'en',
  de: 'de-DE'
}

export function buildHreflangAlternatives() {
  return [
    ...LOCALES.map((item) => ({
      hreflang: HREFLANG_CODES[item.code],
      href: `${SITE_ORIGIN}${localePath(item.code)}`
    })),
    { hreflang: 'x-default', href: `${SITE_ORIGIN}${localePath(PRIMARY_LOCALE)}` }
  ]
}

export function localeFromPath(path: string): Locale {
  const segment = path.split('/').filter(Boolean)[0]
  if (!segment) return PRIMARY_LOCALE
  if (isLocalizedSegment(segment)) return segment
  return PRIMARY_LOCALE
}

export function isLocale(val: string | null | undefined): val is Locale {
  return LOCALES.some((item) => item.code === val)
}

const CRAWLER_PATTERN = /googlebot|google-inspectiontool|googleother|google-extended|apis-google|feedfetcher-google|storebot-google|bingbot|applebot|yandex|duckduckbot|baiduspider|slurp|facebookexternalhit|twitterbot|linkedinbot|semrushbot|ahrefsbot|mj12bot|dotbot/i

export function isSearchCrawler(userAgent: string | undefined): boolean {
  if (!userAgent) return false
  return CRAWLER_PATTERN.test(userAgent)
}

export function detectFromCountry(country: string | undefined): Locale {
  const code = country?.toUpperCase()
  if (code === 'HU') return 'hu'
  if (code === 'DE') return 'de'
  return FALLBACK_LOCALE
}

const COUNTRY_COOKIE = 'archestor-country'

function getCountryCode(): string | undefined {
  if (import.meta.server) {
    const headers = useRequestHeaders(['cf-ipcountry'])
    return headers['cf-ipcountry']
  }

  const countryCookie = useCookie<string | null>(COUNTRY_COOKIE, { path: '/' })
  return countryCookie.value ?? undefined
}

export function syncCountryCookie() {
  if (!import.meta.server || !hasAcceptedConsent() || !hasFunctionalConsent()) return

  const headers = useRequestHeaders(['cf-ipcountry'])
  const country = headers['cf-ipcountry']
  if (!country) return

  const countryCookie = useCookie<string>(COUNTRY_COOKIE, {
    maxAge: 60 * 60 * 24,
    path: '/',
    sameSite: 'lax'
  })
  countryCookie.value = country
}

function readSavedLocale(): Locale | null {
  if (!hasAcceptedConsent() || !hasFunctionalConsent()) return null

  const cookie = useCookie<string | null>(STORAGE_KEY, cookieOptions)
  if (isLocale(cookie.value)) {
    return cookie.value
  }

  if (import.meta.client) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (isLocale(stored)) {
        cookie.value = stored
        return stored
      }
    } catch {}
  }

  return null
}

function persistLocale(next: Locale) {
  if (!hasAcceptedConsent() || !hasFunctionalConsent()) return

  const cookie = useCookie<Locale>(STORAGE_KEY, cookieOptions)
  cookie.value = next

  if (import.meta.client) {
    try { localStorage.setItem(STORAGE_KEY, next) } catch {}
  }
}

export function getSavedLocale(): Locale | null {
  return readSavedLocale()
}

export function hasSavedLocalePreference(): boolean {
  return getSavedLocale() !== null
}

export function detectPreferredLocale(currentPath?: string): Locale {
  const savedLocale = readSavedLocale()
  if (savedLocale) {
    return savedLocale
  }

  const country = getCountryCode()
  if (country) {
    return detectFromCountry(country)
  }

  const path = currentPath ?? (import.meta.client ? window.location.pathname : undefined)
  if (path) {
    const segment = path.split('/').filter(Boolean)[0]
    if (!segment) return PRIMARY_LOCALE
    if (isLocalizedSegment(segment)) return segment
  }

  return FALLBACK_LOCALE
}

export function saveLocaleAfterConsent() {
  if (!hasAcceptedConsent() || !hasFunctionalConsent()) return

  const locale = useState<Locale>('locale')
  persistLocale(locale.value)
}

export function useI18n() {
  const locale = useState<Locale>('locale', () => PRIMARY_LOCALE)

  const messages = computed(() => localeMessages[locale.value])

  const localeUrl = computed(() => `${SITE_ORIGIN}${localePath(locale.value)}`)

  const applyLocale = (next: Locale) => {
    locale.value = next

    if (import.meta.client) {
      document.documentElement.lang = next
    }
  }

  const setLocale = (next: Locale) => {
    if (locale.value === next) return

    applyLocale(next)
    persistLocale(next)

    if (!import.meta.client) return

    const targetPath = localePath(next)

    if (window.location.pathname !== targetPath) {
      const route = useRoute()
      navigateTo({ path: targetPath, query: route.query, hash: route.hash }, { replace: true })
    }
  }

  return { locale, messages, localeUrl, applyLocale, setLocale }
}